import { FEATURES } from '@constants/Text'
import {
  LIMIAR_CRITICO,
  CAP_SECUNDARIO,
} from '@modules/crm/core/relacionamentoScore.js'

export const LEAD_STAGES = [
  { id: 'contact', label: 'Contato' },
  { id: 'registration', label: 'Cadastro' },
  { id: 'finalization', label: 'Finalização' },
  { id: 'opportunity', label: 'Cadastro de oportunidades' },
]

export const OPPORTUNITY_STATUSES = [
  { id: 'pending', label: 'Em negociação' },
  { id: 'won', label: 'Ganha' },
  { id: 'lost', label: 'Perdida' },
]

export const LEAD_STAGE_LABEL = LEAD_STAGES.reduce((acc, stage) => {
  acc[stage.id] = stage.label
  return acc
}, {})

// Rótulos PT-BR do tipo de chamada (API4Com). Fallback no componente: || 'Recebida'.
export const MAP_TIPO_CHAMADA = {
  outbound: 'Realizada',
  inbound: 'Recebida',
}

// Rótulos PT-BR da causa de término (enum HangupCause). Fallback no componente: || valor cru.
export const MAP_CAUSA_TERMINO = {
  NORMAL_CLEARING: 'Encerrada normalmente',
  USER_BUSY: 'Ocupado',
  NO_ANSWER: 'Não atendida',
  NO_USER_RESPONSE: 'Sem resposta',
  ORIGINATOR_CANCEL: 'Cancelada pela origem',
  CALL_REJECTED: 'Rejeitada',
  UNALLOCATED_NUMBER: 'Número inexistente',
  NUMBER_CHANGED: 'Caixa postal',
  ALLOTTED_TIMEOUT: 'Tempo expirado',
}

export const GRUPOS_FAMILIARES_DESCRICAO =
  'Agrupe clientes da mesma família para acompanhar o patrimônio em conjunto.'

export const crmRoutes = () => [
  {
    name: 'crm.welcome',
    title: 'Bem-vindo',
    subtitle: 'Conheça o CRM da AAWZ para o mercado financeiro',
  },
  {
    name: 'crm.overview',
    title: 'Visão Geral',
    subtitle: 'Resumo operacional do CRM',
  },
  {
    name: 'crm.leads',
    title: 'Leads',
    subtitle: 'Acompanhe leads, pipeline e oportunidades',
  },
  {
    name: 'crm.relationship',
    title: 'Relacionamento',
    subtitle: 'Acompanhe prioridades, clientes e rotina mensal',
  },
  {
    name: 'crm.calendar',
    title: 'Calendário',
    subtitle: 'Agenda de tarefas e compromissos',
  },
  {
    // Agrupa Atendimento/Emails/Ligações/Transcrições (ver view-crm-comunicacoes.vue).
    // `children` é obrigatório pro NavBarNavigation realçar a aba nas rotas descendentes.
    // subtitle: fallback estático — crm-nav-bar.vue sobrescreve dinamicamente por feature flag.
    name: 'crm.comunicacoes',
    title: 'Omnichannel',
    subtitle: 'Mensagens, e-mails, transcrições e ligações em um só lugar',
    children: [
      'crm.comunicacoes',
      'crm.atendimento',
      'crm.emails',
      'crm.ligacoes',
      'crm.transcricoes',
      'crm.transcricoes.live',
      'crm.transcricoes.history',
      'crm.transcricoes.dashboard',
      'crm.transcricoes.projects',
    ],
  },
  {
    name: 'crm.opportunities',
    title: 'Oportunidades',
    subtitle: 'Acompanhe as oportunidades de negócio',
  },
  {
    name: 'crm.proposta-r2',
    title: 'Proposta R2',
    subtitle: 'Monte a proposta da segunda reunião',
    // A aba tem que sumir com a flag desligada. O filtro da nav-bar é
    // `!route.feature || canAccessFeature(route.feature)`: entrada SEM `feature`
    // passa sempre. Sem esta linha a aba apareceria para todo escritório com CRM
    // e o clique cairia em 404 — o guard da rota exige
    // `[FEATURES.CRM, FEATURES.CRM_PROPOSTA_R2]` (routes.js), o nav não exigiria.
    feature: FEATURES.CRM_PROPOSTA_R2,
  },
  {
    name: 'crm.grupos-familiares',
    title: 'Grupos Familiares',
    subtitle: GRUPOS_FAMILIARES_DESCRICAO,
    // A aba tem que sumir com a flag desligada. Sem isto ela aparecia e o clique
    // caía em 404 — o guard da rota exige a flag, o nav não exigia.
    feature: FEATURES.FAMILY_GROUP,
  },
]

const BASE_HEADERS = [
  { text: 'Atividade', value: 'titulo' },
  { text: 'Atuar até', value: 'data_prevista' },
  { text: 'Valor', value: 'valor' },
  { text: 'Resultado', value: 'resultado', align: 'end', sortable: false },
]

export const TABLE_RELATIONSHIP_ACTIVITIES_RESULT_HEADERS = {
  tarefas_criticas: BASE_HEADERS,
  relacionamento_atrasadas: BASE_HEADERS,
  tarefas_manuais_atrasadas: BASE_HEADERS,
  tarefas: BASE_HEADERS,
  campanhas: BASE_HEADERS,
  sugestao_alocacao: BASE_HEADERS,
  oportunidades: [
    { text: 'Atividade', value: 'titulo' },
    { text: 'Tipo', value: 'tipo_oportunidade' },
    { text: 'Temperatura', value: 'temperatura' },
    { text: 'Fase', value: 'fase' },
    { text: 'Atuar até', value: 'data_prevista' },
    { text: 'Valor', value: 'valor' },
    { text: 'Ações', value: 'acoes', align: 'end', sortable: false },
  ],
}

export const TABLE_RELATIONSHIP_HEADERS = [
  { text: 'Cliente', value: 'client' },
  { text: 'Detalhes cliente', value: 'client_details' },
  { text: 'Relacionamento', value: 'relationship' },
  { text: '', value: 'nome', align: ' d-none' },
  { text: '', value: 'email', align: ' d-none' },
  { text: '', value: 'data_proxima_atividade', align: ' d-none' },
  { text: '', value: 'tags', align: ' d-none' },
]

// Header granular da view de CLIENTES (modo flat). Uma coluna por valor, para
// mostrar o cabeçalho nativo do BaseTable e deixar cada linha com uma única
// linha de valores. As 2 últimas colunas ficam ocultas (` d-none`) apenas para
// a busca nativa do v-data-table continuar casando email/data. "Último
// contato" e "Atuar até" são ordenáveis via `custom-sort` (@utils/Sort,
// dict.dates) em table-relationship-client.vue — o sort nativo do
// v-data-table compararia a string DD/MM/YYYY alfabeticamente, o que não
// corresponde à ordem cronológica real. "Patrimônio (PL/POT)" agrupa PL +
// potencial no componente ProgressTarget (barra verde + marcador laranja).
export const TABLE_RELATIONSHIP_CLIENT_HEADERS = [
  { text: 'Cliente', value: 'nome', width: '280px' },
  { text: 'Tags', value: 'tags', width: '200px', sortable: false },
  { text: 'Patrimônio (PL/POT)', value: 'pl_total', width: '170px' },
  { text: 'Telefone', value: 'telefone', width: '130px', sortable: false },
  // Conta + Saldo unidos numa coluna (2 linhas) p/ reduzir a largura total e
  // caber sem scroll com a sidebar expandida.
  {
    text: 'Conta / Saldo',
    value: 'codigo_corretora',
    width: '130px',
    sortable: false,
  },
  // Coluna espaçadora (width 100%) absorve a folga da tabela ENTRE "Saldo" e
  // "Último contato": mantém Cliente/Tags/Patrimônio/Saldo compactos à esquerda
  // e agrupa Último contato | Atuar até | ação encostados na borda direita.
  { text: '', value: 'spacer', sortable: false, width: '100%' },
  {
    text: 'Último contato',
    value: 'data_ultima_atividade_f',
    width: '120px',
  },
  {
    text: 'Atuar até',
    value: 'data_proxima_atividade_f',
    width: '110px',
  },
  { text: '', value: 'acoes', align: 'end', sortable: false },
  { text: '', value: 'email', align: ' d-none' },
  { text: '', value: 'data_proxima_atividade', align: ' d-none' },
]

// Header granular da VISÃO POR TAREFAS do relacionamento (modo flat). Mesma
// estrutura da tabela de clientes: uma coluna por valor, header nativo do
// BaseTable e cada linha em uma única linha de valores. A 1ª coluna é a Tarefa
// (título + cliente). A tabela de tarefas não tem busca, então não precisa de
// colunas ocultas (` d-none`) para o filtro nativo. "Último contato" e
// "Atuar até" são ordenáveis via `custom-sort` (@utils/Sort, dict.dates) em
// table-relationship-task.vue — o sort nativo do v-data-table compararia a
// string DD/MM/YYYY alfabeticamente, o que não corresponde à ordem
// cronológica real.
export const TABLE_RELATIONSHIP_TASK_HEADERS = [
  { text: 'Tarefa', value: 'titulo', width: '330px', sortable: false },
  { text: 'Tags', value: 'tags', width: '200px', sortable: false },
  {
    text: 'Patrimônio (PL/POT)',
    value: 'pl_total',
    width: '170px',
    sortable: false,
  },
  { text: 'Telefone', value: 'telefone', width: '130px', sortable: false },
  // Conta + Saldo unidos numa coluna (2 linhas) p/ reduzir a largura total e
  // caber sem scroll com a sidebar expandida.
  {
    text: 'Conta / Saldo',
    value: 'codigo_corretora',
    width: '130px',
    sortable: false,
  },
  // Coluna espaçadora (width 100%) absorve a folga da tabela: agrupa
  // Último contato | Atuar até | ação encostados na borda direita.
  { text: '', value: 'spacer', sortable: false, width: '100%' },
  {
    text: 'Último contato',
    value: 'data_ultima_interacao_f',
    width: '120px',
  },
  {
    text: 'Atuar até',
    value: 'data_prevista',
    width: '110px',
  },
  { text: '', value: 'acoes', align: 'end', sortable: false },
]

export const BASE_RELATIONSHIP_KANBAN_STAGES = [
  { id: 'em_atraso', nome: 'Em atraso', ordem: 1, clients: [] },
  { id: 'hoje', nome: 'Hoje', ordem: 2, clients: [] },
  { id: 'amanha', nome: 'Amanhã', ordem: 3, clients: [] },
  { id: 'essa_semana', nome: 'Essa semana', ordem: 4, clients: [] },
  { id: 'esse_mes', nome: 'Esse mês', ordem: 5, clients: [] },
  { id: 'proximos_meses', nome: 'Próximos meses', ordem: 6, clients: [] },
  { id: 'sem_tarefas', nome: 'Sem tarefas', ordem: 7, clients: [] },
]

export const BASE_CLIENT_TASKS_SECTIONS = [
  { key: 'relacionamento_atrasadas', items: [], index: 1 },
  { key: 'tarefas_manuais_atrasadas', items: [], index: 2 },
  { key: 'tarefas', items: [], index: 3 },
  { key: 'tarefas_criticas', items: [], index: 4 },
  { key: 'campanhas', items: [], index: 5 },
  { key: 'sugestao_alocacao', items: [], index: 6 },
  { key: 'oportunidades', items: [], index: 7 },
]

export const BASE_ACTIVITIES_TASKS_SECTIONS = [
  { key: 'relacionamento_atrasadas', items: [], index: 1 },
  { key: 'tarefas_manuais_atrasadas', items: [], index: 2 },
  { key: 'tarefas', items: [], index: 3 },
  { key: 'tarefas_criticas', items: [], index: 4 },
  { key: 'campanhas', items: [], index: 5 },
  { key: 'sugestao_alocacao', items: [], index: 6 },
  { key: 'oportunidades', items: [], index: 7 },
]

// Fallback exibido quando um cliente/tarefa nunca teve atividade registrada
// (data_ultima_atividade/data_ultima_interacao/data_proxima_atividade nulos).
// A ordenação por data (custom-sort das tabelas de relacionamento) depende
// desse literal não ser parseável como data DD/MM/YYYY.
export const NO_ACTIVITY_LABEL = 'Sem atividade'

// Texto de ajuda da pontuação de criticidade ([US-6], badge em
// relationship-score-badge.vue). Fonte de verdade dos pesos (750 por pendência
// urgente e os menores 250/150/100) é aawz_crm/core/relacionamento_score.py —
// propositalmente NÃO enumerados aqui, pra este texto nunca "mentir" se algum
// peso for ajustado depois. Os números citados vêm interpolados de
// LIMIAR_CRITICO/CAP_SECUNDARIO (core/relacionamentoScore.js), nunca digitados
// — inclusive o "750" da pendência urgente, que por desenho É o próprio
// LIMIAR_CRITICO (uma pendência urgente sozinha já torna o cliente crítico).
export const RELACIONAMENTO_PONTUACAO_AJUDA =
  'Cada pendência do cliente vale pontos, e os pontos somam. A lista é ' +
  'ordenada do maior para o menor. Atividade prioritária, atividade de ' +
  'relacionamento atrasada e tarefa manual atrasada valem ' +
  `${LIMIAR_CRITICO} pontos cada — são as pendências urgentes. Cliente ` +
  `com ${LIMIAR_CRITICO} pontos ou mais é considerado Crítico. ` +
  'Pendências não urgentes (relacionamento a vencer, tarefa em dia, ' +
  'oportunidade aberta) valem menos. "(limitado)" aparece quando os ' +
  'itens não urgentes foram somados até o teto de ' +
  `${CAP_SECUNDARIO} pontos. Esse teto existe para que muitas ` +
  'pendências não urgentes nunca tornem um cliente crítico sozinhas — ' +
  'quem é crítico tem, necessariamente, ao menos uma pendência urgente.'

export const HISTORIC_DEFAULT_PERIOD = 'Esse mês'

export const HISTORIC_ROUTE = 'crm.relationship.historic'

export const ROUTE_FILTER_RESETS = {
  'crm.relationship.clients': [
    { key: 'status', type: 'array' },
    { key: 'origem', type: 'array' },
    { key: 'historic_atividade', type: 'array' },
    { key: 'historic_tipo_contato', type: 'array' },
    { key: 'data_referencia', type: 'null' },
  ],
  'crm.relationship.priorities': [
    { key: 'search', type: 'string' },
    { key: 'historic_atividade', type: 'array' },
    { key: 'historic_tipo_contato', type: 'array' },
    { key: 'data_referencia', type: 'null' },
  ],
  'crm.relationship.historic': [
    { key: 'search', type: 'string' },
    { key: 'status', type: 'array' },
    { key: 'origem', type: 'array' },
    { key: 'historic_atividade', type: 'array' },
    { key: 'historic_tipo_contato', type: 'array' },
    { key: 'data_referencia', type: 'null' },
    { key: 'id_profissional_responsavel', type: 'array' },
    { key: 'grupos_atividade_relacionamento', type: 'array' },
  ],
  'crm.relationship.monthlyRoutine': [
    { key: 'search', type: 'string' },
    { key: 'status', type: 'array' },
    { key: 'origem', type: 'array' },
    { key: 'historic_atividade', type: 'array' },
    { key: 'historic_tipo_contato', type: 'array' },
    { key: 'id_profissional_responsavel', type: 'array' },
    { key: 'grupos_atividade_relacionamento', type: 'array' },
    { key: 'id_regua', type: 'array' },
    { key: 'favorito', type: 'boolean' },
  ],
}

export const RELATIONSHIP_ACTIVITY_NO_TASK_KEY = {
  key: 'fora_listagem',
  label: 'Fora da listagem',
}

// Valor de `item.tipo`/`origem` que identifica ocorrência de régua de relacionamento —
// mesmo literal usado em 3 formas (item.tipo, origem da timeline, tipo do payload de
// MODAL.CRM_EDIT_DATETIME); PR #2236, review HerikR0drigues.
export const TIPO_ATIVIDADE_RELACIONAMENTO = 'relacionamento'

// Chave da seção de régua vencida — usada pelo modal de registro para separar as
// linhas que o usuário curou na mão das que o backend quita em cascata.
export const RELACIONAMENTO_ATRASADAS_SECTION_KEY = 'relacionamento_atrasadas'

// A linha da régua que está sendo registrada no formulário fica desabilitada: quem
// grava o resultado dela é o próprio registro, não a curadoria linha a linha.
export const RELACIONAMENTO_ATIVIDADE_EM_ANDAMENTO_TOOLTIP =
  'Você já está realizando essa atividade de relacionamento'

// Filtro de query string. Opt-in do detalhe de um cliente: pede ao backend TODAS as
// régua-atividades vencidas dele. A listagem do escritório segue colapsada em 1 linha
// por cliente, para não inflar uma rota que já monta 7 grupos.
export const FILTRO_EXPANDIR_ATRASADAS = 'expandir_relacionamento_atrasadas'

// Campos de controle da cascata no payload de crm-historico-atividade-cliente.
// O backend os remove antes do insert — não são colunas da tabela.
export const CAMPO_QUITAR_ATRASADAS = 'quitar_relacionamentos_atrasados'
export const CAMPO_IDS_IGNORAR_CASCATA = 'ids_regua_atividade_ignorar_cascata'

// Sentinela do select de "Tipo de relacionamento" quando a atividade registrada não
// pertence a nenhuma régua. buildPayload normaliza para null, e o backend não dispara
// cascata sem id_regua_atividade — por isso o modal de confirmação também não pergunta.
export const REGUA_NAO_PERTENCE = 'naoPertence'

// Resposta gravada na linha quando o usuário responde Positivo/Negativo.
export const RESPOSTA = {
  sim: 'Sim',
  nao: 'Não',
}

// Tipos de ação da coluna Resultado, usados pela tabela de atividades.
export const RESULT_ACTION_TYPES = {
  positivo: 'positivo',
  negativo: 'negativo',
  reagendar: 'reagendar',
}

export const QUITAR_ATRASADAS_MODAL = {
  title: 'Atividades de relacionamento em atraso',
  confirmLabel: 'Concluir todas',
  cancelLabel: 'Manter em aberto',
}

export const RELATIONSHIP_ACTIVITY_SECTION_NAME_MAP = {
  relacionamento_atrasadas: 'Relacionamentos atrasados',
  tarefas_manuais_atrasadas: 'Tarefas manuais atrasadas',
  tarefas: 'Tarefas manuais',
  tarefas_criticas: 'Atividades prioritárias',
  campanhas: 'Campanhas',
  sugestao_alocacao: 'Sugestão de alocação',
  oportunidades: 'Oportunidades',
  [RELATIONSHIP_ACTIVITY_NO_TASK_KEY.key]:
    RELATIONSHIP_ACTIVITY_NO_TASK_KEY.label,
}

export const BASE_CLIENTS_SECTIONS = [
  {
    key: 'relacionamento_atrasadas',
    label: 'Clientes com atividades de relacionamento atrasadas',
    index: 1,
    items: [],
    total: 0,
  },
  {
    key: 'tarefas_manuais_atrasadas',
    label: 'Clientes com tarefas manuais atrasadas',
    index: 2,
    items: [],
    total: 0,
  },
  // Slug 'criticos' mapeia o grupo do backend "cliente com tarefa
  // auto-sugerida" (waterfall `_classify_client`) — não o novo conceito de
  // criticidade por pontuação (score >= 750). Label renomeado para não colidir
  // com o chip "Crítico" de core/relacionamentoScore.js; a chave permanece.
  {
    key: 'criticos',
    label: 'Clientes com atividade prioritária',
    index: 3,
    items: [],
    total: 0,
  },
  // Slug 'ativos' kept to match backend `grupo.identificador`; label is the
  // generic 'Clientes' since the 'inativos' bucket was removed (CU-86ah32kqr —
  // the ativo/inativo categorization was discontinued on the CRM side).
  {
    key: 'ativos',
    label: 'Clientes',
    index: 4,
    items: [],
    total: 0,
  },
]

export const ORIGIN_OF_CONTACT_OPTIONS = [
  'Atividade',
  'Whatsapp',
  'Ligação',
  'Email',
  'Reunião presencial',
  'Vídeo chamada',
  'Linkedin',
  'Documento',
]

// Rotulos da origem da atividade na timeline table (chip sutil de contexto):
// manual = tarefa manual; relacionamento = regua do cliente; funil = etapa do lead;
// critica = atividade critica automatizada (origemTimeline 'critica' do merge backend).
export const ACTIVITY_ORIGIN_LABELS = {
  manual: 'Manual',
  relacionamento: 'Relacionamento',
  funil: 'Funil',
  critica: 'Prioritária',
}

// Mapeia o tipo de atividade para o nome do icon-svg correspondente.
// Mesmos ícones do seletor de tipo de comentários do CRM (CRM_TIPOS_ATIVIDADES)
// e do helper canônico mapTipoAtividade (utils/activityTypeIcon.js).
export const ACTIVITY_TYPE_ICON = {
  Atividade: 'menu-board',
  Whatsapp: 'whatsapp',
  Ligação: 'call',
  Email: 'sms',
  'Reunião presencial': 'profile-two-user',
  'Vídeo chamada': 'video',
  Linkedin: 'mdi-linkedin',
  Documento: 'document',
}

export const REGUA_RELACIONAMENTO_SEM_REGUA_OPTION = {
  id: '__sem_regua__',
  nome: 'Sem régua',
}

export const NEGATIVE_RESULT_OPTIONS = [
  'Cliente com liquidez comprometida',
  'Cliente sem liquidez',
  'Cliente não gostou',
  'Produto incompatível com o perfil do cliente',
  'Outros',
  'Cliente com recursos alocados em outros investimentos',
  'Cliente aguardando evento de liquidez (venda, resgate, etc.)',
  'Cliente considerou o produto arriscado',
  'Cliente não entendeu a proposta',
  'Cliente acredita que a rentabilidade é baixa',
  'Cliente comparou com proposta de outro escritório',
  'Cliente já é atendido por outro assessor',
  'Cliente optou por manter relação com outro escritório',
  'Cliente não respondeu ao contato',
  'Cliente não compareceu à reunião',
  'Cliente demonstrou falta de interesse no momento',
  'Cliente adiou a decisão',
  'Cliente aguardando melhor momento de mercado',
  'Cliente pretende revisar em outro trimestre',
  'Pendência cadastral ou documental',
  'Cliente não concluiu assinatura de proposta',
]

export const OPPORTUNITY_TYPES = [
  'Aporte',
  'Câmbio',
  'Consórcio',
  'Contratação de Previdência',
  'Crédito',
  'Estruturadas',
  'Mercado de Capitais',
  'Migração Fee Fixo',
  'Portabilidade de Previdência',
  'Seguro',
  'STVM',
  'Transferência de Escritório',
  'Outros',
]

export const OPPORTUNITY_FASE_TYPES = [
  'Oportunidade mapeada',
  'Em Negociação (Pipeline)',
  'Aguardando o aceite',
  'Encerrado',
]

export const OPPORTUNITY_TEMPERATURE_NAMES = {
  quente: 'Quente',
  morno: 'Morno',
  frio: 'Frio',
}

export const TAMANHOS_EMPRESA = [
  '1-10',
  '11-50',
  '51-100',
  '101-500',
  '501-1000',
  '1001-5000',
  '5001-10000',
  '10000+',
]

export const SETORES_ATIVIDADE_EMPRESA = [
  'Agronegócio',
  'Alimentação / Restaurante / Bar',
  'Comércio Varejista',
  'E-commerce',
  'Educação / Treinamento',
  'Engenharia & Construção',
  'Financeiro / Bancário / Seguros',
  'Indústria de Transformação',
  'Logística & Transporte',
  'Saúde / Clínicas / Hospitais',
  'Tecnologia da Informação (TI) / Software',
  'Turismo & Hotelaria',
  'Vestuário / Moda',
  'Serviços Profissionais (Consultoria, Jurídico, Contábil)',
  'Energia / Mineração',
  'Outro',
]

export const RELACIONAMENTO_OPTIONS_EMPRESA = [
  'Sócio',
  'Diretor',
  'Investidor',
  'Fundador',
  'Conselheiro',
  'Fornecedor',
  'Cliente',
  'Outro',
]

export const CARGO_OPTIONS_EMPRESA = [
  'Diretor',
  'CEO',
  'CFO',
  'COO',
  'Gerente',
  'Sócio',
  'Consultor',
  'Outro',
]

export const PARENTESCO_OPTIONS_GRUPO_FAMILIAR = [
  'Pai/Mãe',
  'Filho(a)',
  'Irmão(ã)',
  'Avô(ó)',
  'Neto(a)',
  'Tio(a)',
  'Sobrinho(a)',
  'Primo(a)',
  'Cônjuge',
  'Enteado(a)',
  'Padrasto/Madrasta',
  'Genro/Nora',
  'Sogro(a)',
  'Outro',
]

export const MAP_RELATIONSHIP_SECTION = [
  {
    key: 'numero_clientes',
    title: 'Número de clientes',
    icon: 'profile-two-user',
  },
  {
    key: 'clientes_sem_contato_30_dias',
    title: 'Clientes sem contato 30 dias',
    icon: 'warning-circle',
  },
  {
    key: 'atividades_criticas_tratadas_percentual',
    title: 'Atividades prioritárias tratadas (%)',
    icon: 'percentage-circle',
  },
  {
    key: 'clientes_tarefas_atrasadas',
    title: 'Clientes tarefas atrasadas',
    icon: 'timer-warning',
  },
]

export const MAP_LEADS_SECTION = [
  { key: 'volume_pipe', title: 'Volume Pipe', icon: 'wallet' },
  {
    key: 'leads_sem_contato_7_dias',
    title: 'Leads sem contato 7 dias',
    icon: 'warning-circle',
  },
  {
    key: 'taxa_conversao_90_dias',
    title: 'Taxa conversão 90 dias (%)',
    icon: 'percentage-circle',
  },
  {
    key: 'leads_tarefas_atrasadas',
    title: 'Leads tarefas atrasadas',
    icon: 'timer-warning',
  },
]

export const MAP_OPPORTUNITIES_SECTION = [
  { key: 'total', title: 'Total', icon: 'lamp-on' },
  {
    key: 'oportunidades_prospeccao',
    title: 'Oportunidades prospecção',
    icon: 'status-up',
  },
  {
    key: 'oportunidades_relacionamento',
    title: 'Oportunidades relacionamento',
    icon: 'user-tag',
  },
  {
    key: 'oportunidades_atrasadas',
    title: 'Oportunidades atrasadas',
    icon: 'timer-warning',
  },
]

export const KYC_BOOLEAN_FIELDS = [
  'esta_empregado',
  'possui_multiplas_fontes_renda',
  'declara_imposto_renda',
  'e_empresario',
  'e_socio_empresa',
  'tem_cobranca_ativa',
  'e_pessoa_politicamente_exposta',
  'possui_sancao_ativa',
  'possui_historico_sancoes',
  'e_doador_eleitoral',
  'provavelmente_endividado',
  'recebe_beneficio_social',
  'e_chefe_familia',
  'e_provavel_dependente',
  'e_aposentado',
  'e_estudante',
  'possui_filhos',
  'casou_recentemente',
  'divorciou_recentemente',
  'comprou_imovel_recentemente',
  'mudou_emprego_recentemente',
]

export const KYC_INTEGER_FIELDS = [
  'idade',
  'quantidade_vinculos_empresariais',
  'quantidade_empregos',
  'total_processos_judiciais',
  'processos_como_autor',
  'processos_como_reu',
  'processos_ultimos_12_meses',
  'meses_consecutivos_em_cobranca',
  'total_historico_cobrancas',
  'total_dividas_governo',
  'total_sancoes',
  'score_risco_financeiro',
  'quantidade_veiculos',
  'quantidade_enderecos',
  'quantidade_dependentes',
  'quantidade_servidores_publicos_familia',
  'quantidade_membros_com_beneficio',
  'quantidade_programas_sociais',
  'quantidade_instituicoes_relacionadas',
]

export const KYC_FLOAT_FIELDS = [
  'valor_total_dividas_governo',
  'probabilidade_endividamento',
  'valor_total_beneficios',
  'score_risco_financeiro_familiar',
]

export const KYC_DATE_FIELDS = [
  'dados_financeiros_atualizados_em',
  'data_ultimo_beneficio',
]

export const KYC_DEFAULT_DATA = {
  id: '',
  cpf: '',
  nome: '',
  data_nascimento: '',
  idade: '',
  pais_fiscal: '',
  estado_residencia_fiscal: '',
  faixa_renda: '',
  esta_empregado: false,
  profissao: '',
  cargo_profissional: '',
  empresa_atual: '',
  setor_profissional: '',
  renda_estimada: '',
  possui_multiplas_fontes_renda: false,
  faixa_patrimonio: '',
  dados_financeiros_atualizados_em: '',
  declara_imposto_renda: false,
  ano_ultima_declaracao_ir: '',
  situacao_ultima_declaracao_ir: '',
  quantidade_vinculos_empresariais: '',
  quantidade_empregos: '',
  e_empresario: false,
  e_socio_empresa: false,
  total_processos_judiciais: '',
  processos_como_autor: '',
  processos_como_reu: '',
  processos_ultimos_12_meses: '',
  tem_cobranca_ativa: false,
  meses_consecutivos_em_cobranca: '',
  total_historico_cobrancas: '',
  total_dividas_governo: '',
  valor_total_dividas_governo: '',
  e_pessoa_politicamente_exposta: false,
  nivel_exposicao_politica: '',
  cargo_exposicao_politica: '',
  possui_sancao_ativa: false,
  possui_historico_sancoes: false,
  total_sancoes: '',
  e_doador_eleitoral: false,
  score_risco_financeiro: '',
  nivel_risco_financeiro: '',
  provavelmente_endividado: false,
  probabilidade_endividamento: '',
  id_consulta_provedor: '',
  data_consulta_provedor: '',
  quantidade_veiculos: '',
  quantidade_enderecos: '',
  endereco_mais_recente: '',
  faixa_renda_familiar: '',
  quantidade_dependentes: '',
  e_chefe_familia: false,
  e_provavel_dependente: false,
  faixa_renda_livre_mensal: '',
  faixa_gastos_mensais: '',
  quantidade_servidores_publicos_familia: '',
  principal_fonte_renda_familiar: '',
  recebe_beneficio_social: false,
  quantidade_membros_com_beneficio: '',
  quantidade_programas_sociais: '',
  valor_total_beneficios: '',
  data_ultimo_beneficio: '',
  score_risco_financeiro_familiar: '',
  nivel_risco_financeiro_familiar: '',
  e_aposentado: false,
  e_estudante: false,
  possui_filhos: false,
  casou_recentemente: false,
  divorciou_recentemente: false,
  comprou_imovel_recentemente: false,
  mudou_emprego_recentemente: false,
  nivel_interesse_financeiro: '',
  quantidade_instituicoes_relacionadas: '',
}

export const KYC_FORM_SECTIONS = [
  {
    title: 'Renda e Ocupação',
    fields: [
      { key: 'renda_estimada', label: 'Renda Estimada', md: 4 },
      { key: 'faixa_renda', label: 'Faixa de Renda', md: 4 },
      { key: 'profissao', label: 'Profissão', md: 4 },
      { key: 'cargo_profissional', label: 'Cargo', md: 4 },
      { key: 'empresa_atual', label: 'Empresa Atual', md: 4 },
      { key: 'setor_profissional', label: 'Setor Profissional', md: 4 },
      {
        key: 'possui_multiplas_fontes_renda',
        label: 'Possui Múltiplas Fontes de Renda',
        type: 'checkbox',
        md: 4,
      },
      {
        key: 'esta_empregado',
        label: 'Está Empregado',
        type: 'checkbox',
        md: 4,
      },
    ],
  },
  {
    title: 'Patrimônio e Endereços',
    fields: [
      { key: 'faixa_patrimonio', label: 'Faixa de Patrimônio', md: 4 },
      {
        key: 'dados_financeiros_atualizados_em',
        label: 'Dados Financeiros Atualizados em',
        type: 'date',
        md: 4,
      },
      {
        key: 'quantidade_veiculos',
        label: 'Quantidade de Veículos',
        type: 'number',
        md: 4,
      },
      {
        key: 'quantidade_enderecos',
        label: 'Quantidade de Endereços',
        type: 'number',
        md: 4,
      },
      {
        key: 'endereco_mais_recente',
        label: 'Endereço Mais Recente',
        type: 'textarea',
        md: 4,
      },
    ],
  },
  {
    title: 'Imposto de Renda',
    fields: [
      {
        key: 'ano_ultima_declaracao_ir',
        label: 'Ano da Última Declaração',
        md: 4,
      },
      {
        key: 'situacao_ultima_declaracao_ir',
        label: 'Situação da Última Declaração',
        md: 4,
      },
      {
        key: 'declara_imposto_renda',
        label: 'Declara Imposto de Renda',
        type: 'checkbox',
        md: 4,
        inline: true,
      },
    ],
  },
  {
    title: 'Relacionamentos Empresariais',
    fields: [
      {
        key: 'quantidade_vinculos_empresariais',
        label: 'Quantidade de Vínculos Empresariais',
        type: 'number',
        md: 4,
      },
      {
        key: 'quantidade_empregos',
        label: 'Quantidade de Empregos',
        type: 'number',
        md: 4,
      },
      {
        key: 'e_empresario',
        label: 'É Empresário',
        type: 'checkbox',
        md: 4,
        inline: true,
      },
      {
        key: 'e_socio_empresa',
        label: 'É Sócio de Empresa',
        type: 'checkbox',
        md: 4,
      },
    ],
  },
  {
    title: 'Processos e Riscos',
    fields: [
      {
        key: 'total_processos_judiciais',
        label: 'Total de Processos Judiciais',
        type: 'number',
        md: 4,
      },
      {
        key: 'processos_como_autor',
        label: 'Processos como Autor',
        type: 'number',
        md: 4,
      },
      {
        key: 'processos_como_reu',
        label: 'Processos como Réu',
        type: 'number',
        md: 4,
      },
      {
        key: 'processos_ultimos_12_meses',
        label: 'Processos nos Últimos 12 Meses',
        type: 'number',
        md: 4,
      },
    ],
  },
  {
    title: 'Cobranças',
    fields: [
      {
        key: 'meses_consecutivos_em_cobranca',
        label: 'Meses Consecutivos em Cobrança',
        type: 'number',
        md: 4,
      },
      {
        key: 'total_historico_cobrancas',
        label: 'Total Histórico de Cobranças',
        type: 'number',
        md: 4,
      },
      {
        key: 'tem_cobranca_ativa',
        label: 'Possui Cobrança Ativa',
        type: 'checkbox',
        md: 4,
        inline: true,
      },
    ],
  },
  {
    title: 'Dívidas Governamentais',
    fields: [
      {
        key: 'total_dividas_governo',
        label: 'Total de Dívidas',
        type: 'number',
        md: 4,
      },
      {
        key: 'valor_total_dividas_governo',
        label: 'Valor Total das Dívidas',
        type: 'number',
        md: 4,
      },
    ],
  },
  {
    title: 'Compliance',
    fields: [
      {
        key: 'nivel_exposicao_politica',
        label: 'Nível de Exposição Política',
        md: 4,
      },
      {
        key: 'cargo_exposicao_politica',
        label: 'Cargo de Exposição Política',
        md: 4,
      },
      {
        key: 'total_sancoes',
        label: 'Total de Sanções',
        type: 'number',
        md: 4,
      },
      {
        key: 'e_pessoa_politicamente_exposta',
        label: 'É Pessoa Politicamente Exposta',
        type: 'checkbox',
        md: 4,
      },
      {
        key: 'possui_sancao_ativa',
        label: 'Possui Sanção Ativa',
        type: 'checkbox',
        md: 4,
      },
      {
        key: 'possui_historico_sancoes',
        label: 'Possui Histórico de Sanções',
        type: 'checkbox',
        md: 4,
      },
      {
        key: 'e_doador_eleitoral',
        label: 'É Doador Eleitoral',
        type: 'checkbox',
        md: 4,
      },
    ],
  },
  {
    title: 'Risco Financeiro Individual',
    fields: [
      {
        key: 'score_risco_financeiro',
        label: 'Score de Risco',
        type: 'number',
        md: 4,
      },
      {
        key: 'nivel_risco_financeiro',
        label: 'Nível de Risco',
        md: 4,
      },
    ],
  },
  {
    title: 'Probabilidade de Endividamento',
    fields: [
      {
        key: 'probabilidade_endividamento',
        label: 'Probabilidade de Endividamento',
        type: 'number',
        md: 4,
      },
      {
        key: 'provavelmente_endividado',
        label: 'Provavelmente Endividado',
        type: 'checkbox',
        md: 4,
        inline: true,
      },
    ],
  },
  // {
  //   title: 'Metadados da Consulta',
  //   fields: [
  //     {
  //       key: 'id_consulta_provedor',
  //       label: 'ID da Consulta',
  //       md: 4,
  //     },
  //     {
  //       key: 'data_consulta_provedor',
  //       label: 'Data da Consulta',
  //       type: 'date',
  //       md: 4,
  //     },
  //   ],
  // },
  {
    title: 'Dados Familiares',
    fields: [
      {
        key: 'faixa_renda_familiar',
        label: 'Faixa de Renda Familiar',
        md: 4,
      },
      {
        key: 'quantidade_dependentes',
        label: 'Quantidade de Dependentes',
        type: 'number',
        md: 4,
      },

      {
        key: 'faixa_renda_livre_mensal',
        label: 'Faixa de Renda Livre Mensal',
        md: 4,
      },
      {
        key: 'faixa_gastos_mensais',
        label: 'Faixa de Gastos Mensais',
        md: 4,
      },
      {
        key: 'quantidade_servidores_publicos_familia',
        label: 'Servidores Públicos na Família',
        type: 'number',
        md: 4,
      },
      {
        key: 'principal_fonte_renda_familiar',
        label: 'Principal Fonte de Renda Familiar',
        md: 4,
      },
      {
        key: 'e_chefe_familia',
        label: 'É Chefe de Família',
        type: 'checkbox',
        md: 4,
      },
      {
        key: 'e_provavel_dependente',
        label: 'É Provável Dependente',
        type: 'checkbox',
        md: 4,
      },
    ],
  },
  {
    title: 'Benefícios Sociais',
    fields: [
      {
        key: 'quantidade_membros_com_beneficio',
        label: 'Membros com Benefício',
        type: 'number',
        md: 4,
      },
      {
        key: 'quantidade_programas_sociais',
        label: 'Programas Sociais',
        type: 'number',
        md: 4,
      },
      {
        key: 'valor_total_beneficios',
        label: 'Valor Total dos Benefícios',
        type: 'number',
        md: 4,
      },
      {
        key: 'data_ultimo_beneficio',
        label: 'Data do Último Benefício',
        type: 'date',
        md: 4,
      },
      {
        key: 'recebe_beneficio_social',
        label: 'Recebe Benefício Social',
        type: 'checkbox',
        md: 4,
        inline: true,
      },
    ],
  },
  {
    title: 'Risco Financeiro Familiar',
    fields: [
      {
        key: 'score_risco_financeiro_familiar',
        label: 'Score de Risco Familiar',
        type: 'number',
        md: 4,
      },
      {
        key: 'nivel_risco_financeiro_familiar',
        label: 'Nível de Risco Familiar',
        md: 4,
      },
    ],
  },
  {
    title: 'Estágios de Vida',
    fields: [
      {
        key: 'e_aposentado',
        label: 'É Aposentado',
        type: 'checkbox',
        md: 3,
      },
      {
        key: 'e_estudante',
        label: 'É Estudante',
        type: 'checkbox',
        md: 3,
      },
      {
        key: 'possui_filhos',
        label: 'Possui Filhos',
        type: 'checkbox',
        md: 3,
      },
      {
        key: 'casou_recentemente',
        label: 'Casou Recentemente',
        type: 'checkbox',
        md: 3,
      },
      {
        key: 'divorciou_recentemente',
        label: 'Divorciou Recentemente',
        type: 'checkbox',
        md: 3,
      },
      {
        key: 'comprou_imovel_recentemente',
        label: 'Comprou Imóvel Recentemente',
        type: 'checkbox',
        md: 3,
      },
      {
        key: 'mudou_emprego_recentemente',
        label: 'Mudou de Emprego Recentemente',
        type: 'checkbox',
        md: 3,
      },
    ],
  },
  {
    title: 'Interesses Financeiros',
    fields: [
      {
        key: 'nivel_interesse_financeiro',
        label: 'Nível de Interesse Financeiro',
        md: 4,
      },
      {
        key: 'quantidade_instituicoes_relacionadas',
        label: 'Quantidade de Instituições Relacionadas',
        type: 'number',
        md: 4,
      },
    ],
  },
]

const KYC_FINANCIAL_SECTION_TITLES = new Set([
  'Renda e Ocupação',
  'Patrimônio e Endereços',
  'Imposto de Renda',
  'Relacionamentos Empresariais',
  'Cobranças',
  'Dívidas Governamentais',
  'Risco Financeiro Individual',
  'Probabilidade de Endividamento',
  'Benefícios Sociais',
  'Risco Financeiro Familiar',
  'Interesses Financeiros',
])

const KYC_REGULATORY_SECTION_TITLES = new Set([
  'Processos e Riscos',
  'Compliance',
  'Metadados da Consulta',
  'Dados Familiares',
  'Estágios de Vida',
])

export const KYC_FINANCIAL_FORM_SECTIONS = KYC_FORM_SECTIONS.filter((section) =>
  KYC_FINANCIAL_SECTION_TITLES.has(section.title)
)

export const KYC_REGULATORY_FORM_SECTIONS = KYC_FORM_SECTIONS.filter(
  (section) => KYC_REGULATORY_SECTION_TITLES.has(section.title)
)

export const TIMELINE_TASK_STATUS = {
  DONE: 'done',
  PENDING: 'pending',
  NEUTRAL: 'neutral',
  RESULTADO_CONCLUIDO: 'concluido',
  STATUS_EXECUTADA: 'executada',
  STATUS_PENDENTE: 'pendente',
  STATUS_ATRASADA: 'atrasada',
}

// spec-001 `tarefas-fixas-atrasadas-sem-prazo` (US-4/AC-4.2) — rótulo + ícone
// das duas seções FIXAS do card "Próximas Tarefas" (ficha do cliente):
// Atrasadas e Sem prazo. Termo de produto e ícones copiados de
// modules/tarefas/constants/Text.js:449-451 (mesmo par que QUICK_VIEWS usa na
// Lista de Tarefas) — não redeclare em outro lugar.
//
// Shape consumido pela prop `fixedSections` do CrmTimelineTable
// (contracts/ui-components.md); o consumidor (task-steps-lead) injeta
// `items` por cima de cada entrada ao montar o array.
// `tone` mapeia para token de cor no componente consumidor (ex.: `--danger`),
// NUNCA para hex; `null` = seção usa a cor neutra padrão.
export const TIMELINE_FIXED_SECTIONS = [
  { key: 'overdue', label: 'Atrasadas', icon: 'warning', tone: 'danger' },
  { key: 'noDue', label: 'Sem prazo', icon: 'calendar-edit', tone: null },
]

// Domínio completo de status do lead (mesmo conjunto de LEAD_STATUS_OPTIONS_MAP,
// mais abaixo neste arquivo — 6 valores). NOVO_LEGADO e PERDIDO_LEGADO são
// aliases legados (existiam antes de EM_PROGRESSO/PERDA virarem os nomes
// atuais e ainda aparecem em leads antigos nunca migrados); tratados como
// sinônimo de EM_PROGRESSO/PERDA em toda a lógica de estado, nunca como
// status à parte. Fonte única — evita literal espalhado pelos componentes
// que decidem transição de status (LeadGanhoPerdaToggle, crm-lead-steps.vue).
export const LEAD_STATUS = {
  EM_PROGRESSO: 'em_progresso',
  NOVO_LEGADO: 'novo',
  PENDENTE: 'pendente',
  CONVERTIDO: 'convertido',
  PERDA: 'perda',
  PERDIDO_LEGADO: 'perdido',
}

export const LEAD_STATUS_NAME = {
  [LEAD_STATUS.EM_PROGRESSO]: 'Em progresso',
  [LEAD_STATUS.CONVERTIDO]: 'Convertido',
  [LEAD_STATUS.PERDA]: 'Perda',
}

// Status que tiram o lead do pipeline (o endpoint do kanban não os devolve
// após reload — ver crm-leads-kanban).
export const LEAD_STATUS_FECHADOS = [
  LEAD_STATUS.CONVERTIDO,
  LEAD_STATUS.PERDA,
  LEAD_STATUS.PERDIDO_LEGADO,
]

export const ESTADO_CIVIL = [
  'Solteiro(a)',
  'Casado(a)',
  'Divorciado(a)',
  'Viúvo(a)',
  'Separado(a)',
  'União Estável',
]

export const DEFAULT_MANUAL_TASK_TYPE = 'manual'

const COMPONENT_HEIGHT_LEAD_MODAL_BY_MODE = {
  lead: {
    'timeline-table': { card: '470px', loading: 480, table: 344 },
    'suggested-text': { card: '470px', loading: 480 },
    comments: { card: '622px', loading: 620 },
  },
  cliente: {
    'timeline-table': { card: '480px', loading: 480, table: 354 },
    'suggested-text': { card: '480px', loading: 480 },
    comments: { card: '620px', loading: 620 },
  },
  oportunidade: {
    'timeline-table': { card: '470px', loading: 480, table: 344 },
    'suggested-text': { card: '470px', loading: 480 },
    comments: { card: '622px', loading: 620 },
  },
}

export const getComponentHeightLeadModal = (mode = 'lead') =>
  COMPONENT_HEIGHT_LEAD_MODAL_BY_MODE[mode] ||
  COMPONENT_HEIGHT_LEAD_MODAL_BY_MODE.lead

export const COMPONENT_HEIGHT_LEAD_MODAL = getComponentHeightLeadModal()

export const LEAD_ORIGEM_OPTIONS_MAP = {
  evento: 'Evento',
  indicacao: 'Indicação',
  busca_ativa: 'Busca ativa',
  formulario: 'Formulário',
  corretora: 'Corretora',
}

export const LEAD_ORIGEM_FORA_DA_LISTAGEM_OPTION = {
  id: '__fora__',
  nome: 'Fora da listagem',
}

export const LEAD_STATUS_OPTIONS_MAP = {
  novo: 'Novo',
  em_progresso: 'Em progresso',
  pendente: 'Pendente',
  convertido: 'Convertido',
  perda: 'Perda',
  perdido: 'Perda',
}

export const BASE_ROUTINE_STAGES = [
  {
    id: 'aguardando_extrato',
    nome: 'Aguardando extrato',
    ordem: 1,
    total_clientes: 0,
    clientes: [],
  },
  {
    id: 'aguardando_processamento',
    nome: 'Aguardando processamento',
    ordem: 2,
    total_clientes: 0,
    clientes: [],
  },
  {
    id: 'em_processamento',
    nome: 'Em processamento',
    ordem: 3,
    total_clientes: 0,
    clientes: [],
  },
  {
    id: 'finalizado_ressalva',
    nome: 'Finalizado com ressalva',
    ordem: 4,
    total_clientes: 0,
    clientes: [],
  },
  {
    id: 'finalizado',
    nome: 'Finalizado',
    ordem: 5,
    total_clientes: 0,
    clientes: [],
  },
]

export const LEAD_TEMPERATURA = [
  { text: 'Quente', value: 'quente' },
  { text: 'Morno', value: 'morno' },
  { text: 'Frio', value: 'frio' },
]

export const PATRIMONIO_VIEW_TABELA = 'tabela'
export const PATRIMONIO_VIEW_GRAFICO = 'grafico'

export const PATRIMONIO_VIEW_OPTIONS = [
  { value: PATRIMONIO_VIEW_TABELA, icon: 'mdi-table', tooltip: 'Tabela' },
  {
    value: PATRIMONIO_VIEW_GRAFICO,
    icon: 'chart-bar-new-ux',
    tooltip: 'Gráfico',
  },
]

export const PATRIMONIO_CARTEIRA_HEADERS = [
  { text: 'Ativo', value: 'nome_ativo' },
  { text: 'Tipo', value: 'tipo' },
  { text: 'Classe', value: 'classe' },
  { text: 'Corretora', value: 'corretora' },
  { text: 'PL Atual', value: 'pl_atual', align: 'end' },
]

export const PATRIMONIO_BENS_HEADERS = [
  { text: 'Nome', value: 'nome_ativo' },
  { text: 'Descrição', value: 'descricao' },
  { text: 'Valor de Mercado', value: 'valor_mercado', align: 'end' },
]

export const MOCK_CARTEIRA_ASSETS = [
  {
    nome_ativo: 'CDB Banco XYZ 120% CDI',
    tipo: 'Renda Fixa',
    classe: 'RF Pós-Fixado',
    corretora: 'Corretora ABC',
    pl_atual: 45230.5,
  },
  {
    nome_ativo: 'Fundo Multimercado Alpha',
    tipo: 'Fundos',
    classe: 'Multimercado',
    corretora: 'Banco DEF',
    pl_atual: 82100,
  },
  {
    nome_ativo: 'PETR4',
    tipo: 'Ação',
    classe: 'Renda Variável',
    corretora: 'Corretora ABC',
    pl_atual: 15700,
  },
  {
    nome_ativo: 'Tesouro IPCA+ 2029',
    tipo: 'Renda Fixa',
    classe: 'RF Inflação',
    corretora: 'Banco DEF',
    pl_atual: 33450.8,
  },
  {
    nome_ativo: 'HGLG11',
    tipo: 'FII',
    classe: 'Fundo Imobiliário',
    corretora: 'Corretora ABC',
    pl_atual: 21000,
  },
]

export const MOCK_CARTEIRA_INFO = {
  totalInvestido: 0,
  totalInvestidoMask: 'R$ XXX.XXX,XX',
  distribuicao: [
    { corretora: 'Corretora ABC', valor: 81930.5, percentual: 41.5 },
    { corretora: 'Banco DEF', valor: 115550.8, percentual: 58.5 },
  ],
  rentabilidade: { total: 'XX,X', ano: 'XX,X', mes: 'X,X' },
}

export const EMPRESAS_VINCULOS_HEADERS = [
  { text: 'Nome', value: 'nome' },
  { text: 'Relacionamento', value: 'relacionamento' },
  { text: 'Cargo', value: 'cargo' },
]

export const GRUPOS_MEMBROS_HEADERS = [
  { text: 'Nome', value: 'nome' },
  { text: 'Parentesco', value: 'parentesco' },
  { text: 'PL potencial', value: 'pl_potencial_f' },
]

// Mesmas colunas da aba do cliente (reuso de aprendizado) mais a coluna de
// ação, que só existe na listagem — onde é possível remover o vínculo.
export const GRUPOS_MEMBROS_LISTAGEM_HEADERS = GRUPOS_MEMBROS_HEADERS.concat([
  { text: 'Ações', value: 'acoes', align: 'center', sortable: false },
])

export const CONVERSAS_DIRECTION_OPTIONS = [
  { value: 'todos', text: 'Todos' },
  { value: 'recebido', text: 'Recebidos' },
  { value: 'enviado', text: 'Enviados' },
]

export const ENTITY_TYPES = {
  LEAD: 'lead',
  CLIENTE: 'cliente',
}

export const ENTITY_OPTIONS = [
  { text: 'Lead', value: ENTITY_TYPES.LEAD },
  { text: 'Cliente', value: ENTITY_TYPES.CLIENTE },
]

// Contrato FROZEN entre campo-obrigatorio-picker.vue e
// funil-campos-obrigatorios-board.vue (evento de retorno do picker + tipo de
// alvo etapa/Ganho) — não renomear sem sincronizar os dois lados.
export const CRM_CAMPO_OBRIGATORIO_PICKER_RESULT_EVENT =
  'CRM_CAMPO_OBRIGATORIO_PICKER_RESULT'

export const CAMPO_OBRIGATORIO_TARGET_TYPE = {
  ETAPA: 'etapa',
  GANHO: 'ganho',
}

// Índices do wizard de 3 etapas do funil (CrmFunilBuilderModal + FunilBuilder
// — o modal controla o step ativo, o builder reage a ele via prop/watch).
export const FUNIL_BUILDER_WIZARD_STEP = {
  INFORMACOES: 0,
  ETAPAS_ATIVIDADES: 1,
  CAMPOS_OBRIGATORIOS: 2,
}

// --- Transcrições (CRM) ---------------------------------------------------
// As abas (Ao vivo / Histórico / Dashboard / Projetos) agora são rotas próprias;
// a sub-navegação vive em @modules/crm/utils/transcricoes-sub-nav.

// Tipos de projeto (espelha o enum VALID_PROJECT_TYPES do backend). value = slug
// persistido; text = rótulo amigável exibido na UI.
export const TRANSCRIPT_PROJECT_TYPE_OPTIONS = [
  { value: 'interno', text: 'Interno' },
  { value: 'comercial-prospeccao', text: 'Comercial — Prospecção' },
  { value: 'comercial-relacionamento', text: 'Comercial — Relacionamento' },
  { value: 'externo-estrategico', text: 'Externo — Estratégico' },
]

// Mapa slug → rótulo amigável, para exibir o tipo na listagem de projetos.
export const TRANSCRIPT_PROJECT_TYPE_LABEL =
  TRANSCRIPT_PROJECT_TYPE_OPTIONS.reduce((acc, option) => {
    acc[option.value] = option.text
    return acc
  }, {})

export const TRANSCRIPT_MEETING_STATUS_OPTIONS = [
  { text: 'Finalizado', value: 'ended' },
  { text: 'Agendado', value: 'scheduled' },
  { text: 'Erro', value: 'failed' },
]

export const TRANSCRIPT_AI_STATUS_OPTIONS = [
  { text: 'Aguardando', value: 'pending' },
  { text: 'Na fila', value: 'queued' },
  { text: 'Processando IA', value: 'processing' },
  { text: 'IA Concluído', value: 'completed' },
  { text: 'Erro IA', value: 'failed' },
  { text: 'Ignorado', value: 'skipped' },
]

export const TRANSCRIPT_SENTIMENT_OPTIONS = [
  { text: 'Positivo', value: 'positivo' },
  { text: 'Neutro', value: 'neutro' },
  { text: 'Negativo', value: 'negativo' },
]

export const DIVIDER_KEY = '__divider__'

// Gera um id único pra cada divider inserido (permite múltiplas instâncias
// no layout, cada uma com identifier distinto).
export const buildDividerKey = () => {
  const rand = Math.random().toString(36).slice(2, 10)
  return `__divider_${rand}__`
}

// ---- Editor da Visão Geral personalizável (overview-layout-form) ----

// Larguras suportadas por bloco (espelha coluna `width` do contrato e o
// WIDTH_TO_COLS do block-registry). Ordem = do menor pro maior.
export const OVERVIEW_WIDTH_OPTIONS = [
  { value: 'quarter', label: '1/4', cols: 3 },
  { value: 'third', label: '1/3', cols: 4 },
  { value: 'half', label: '1/2', cols: 6 },
  { value: 'full', label: 'Inteiro', cols: 12 },
]

export const OVERVIEW_DEFAULT_WIDTH = 'full'

// Subconjunto curado de ícones (nomes reais de icon-svg) oferecidos no picker
// do divisor. Mantém a escolha enxuta e semântica pro contexto de seções.
export const OVERVIEW_DIVIDER_ICONS = [
  'category',
  'categories',
  'wallet',
  'coin',
  'dollar-circle',
  'dollar-square',
  'chart-finance',
  'chart-bar',
  'chart-grow',
  'analyze-market',
  'crm',
  'users',
  'user',
  'crown',
  'star',
  'home',
  'base-office',
  'document',
  'document-text',
  'calendar',
  'clock',
  'bell',
]

// Rótulos PT-BR dos módulos pra agrupar a paleta de blocos.
export const OVERVIEW_MODULE_LABELS = {
  consolidador: 'Consolidador',
  portfolio_review: 'Portfólio / Simulações',
  financial_planning: 'Planejamento Financeiro',
  compliance: 'Compliance',
  crm: 'CRM',
  campos_personalizados: 'Campos Personalizados',
}

export const OVERVIEW_MODULE_ORDER = [
  'consolidador',
  'portfolio_review',
  'financial_planning',
  'compliance',
  'crm',
  // Campos personalizados sempre por último na paleta — é a seção dinâmica
  // (1 item por campo ativo do escritório), abaixo dos blocos fixos.
  'campos_personalizados',
]

// Identificador do módulo/seção sintético dos campos personalizados na Visão
// Geral. Espelha MODULE_CUSTOM_FIELDS do backend (overview_block_catalog.py)
// e o literal `block_source: 'custom'` do catálogo — sem tradução PT-BR/EN.
export const OVERVIEW_MODULE_CUSTOM_FIELDS = 'campos_personalizados'

// Ícone de cada módulo na paleta do editor — espelha o ícone usado na nav-bar
// do módulo correspondente (src/components/nav-bar.vue).
export const OVERVIEW_MODULE_ICONS = {
  consolidador: 'donut',
  portfolio_review: 'personalCard',
  financial_planning: 'mdi-chart-timeline-variant',
  compliance: 'book-cover',
  crm: 'crm',
}

// spec-005/007 — alerta de sugestões de IA pendentes. Domain-agnostic: usado na
// linha da transcrição (CRM > Transcrições > Histórico), no painel lateral de
// curadoria da reunião, na tabela/kanban de Relacionamento e no Kanban de Leads.
export const IA_SUGGESTIONS_PENDING_BADGE = {
  vazio: '—',
  // Indefinido ≠ vazio: quando o backend não conseguiu apurar, não afirmamos
  // "sem pendências" — mostramos o mesmo travessão, mas sem tooltip nenhum.
  indefinido: '—',
  aria: (n) => `${n} ${n === 1 ? 'sugestão pendente' : 'sugestões pendentes'}`,
  titulo: (n) =>
    `${n} ${n === 1 ? 'sugestão pendente' : 'sugestões pendentes'}`,
  cliqueParaRevisar: 'Clique para revisar',
}

// spec-005 — filtro "Sugestões" na barra de filtros do histórico de transcrições.
export const TRANSCRIPT_TEM_SUGESTOES_OPTIONS = [
  { text: 'Com pendentes', value: 'sim' },
  { text: 'Sem pendentes', value: 'nao' },
]

// Histórico de transcrições agrupado por período (redesign da aba Histórico).
// A ORDEM é significativa em dois pontos: define a sequência dos cards na tela e
// determina qual é o "primeiro grupo não-vazio" — o único que nasce expandido.
// Consumido por `groupTranscriptionsByDate` (@modules/crm/utils).
export const TRANSCRIPT_DATE_GROUPS = [
  { key: 'today', label: 'Hoje' },
  { key: 'yesterday', label: 'Ontem' },
  { key: 'thisWeek', label: 'Esta semana' },
  { key: 'lastWeek', label: 'Semana passada' },
  { key: 'thisMonth', label: 'Este mês' },
  { key: 'older', label: 'Mais antigas' },
]

// Rótulos da linha do tempo do histórico (header do grupo + linha da tabela).
// Singular/plural ficam separados para compor com `pluralize` de @utils/Format.
export const TRANSCRIPT_HISTORY_TEXT = {
  transcricaoSingular: 'transcrição',
  transcricaoPlural: 'transcrições',
  pendenteSingular: 'pendente',
  pendentePlural: 'pendentes',
  semProjeto: 'Sem projeto',
  semCliente: 'Sem cliente',
  semTranscricoes: 'Nenhuma transcrição neste período',
  emAndamento: 'Em andamento',
  nota: 'Nota',
  semValor: '-',
  verDetalhe: 'Ver detalhe',
  editar: 'Editar',
  // Rótulos do cabeçalho da tabela compacta (redesign v2 — colunas reais em
  // vez de card empilhado).
  tituloColunaReuniao: 'Reunião',
  tituloColunaProjeto: 'Projeto',
  tituloColunaCliente: 'Cliente',
  tituloColunaProfissional: 'Profissional',
  tituloColunaQuando: 'Quando',
  tituloColunaSentimento: 'Sentimento',
  tituloColunaAcoes: '',
  // Faixa de indicadores + controles da aba Histórico. "Ao vivo" tem contagem
  // própria (request dedicado com status=in_progress): a listagem é paginada, e
  // uma reunião em andamento pode estar fora do lote carregado.
  cardAoVivo: 'Ao vivo',
  cardAoVivoDescricao: 'Reuniões acontecendo agora',
  cardHistorico: 'Histórico',
  cardHistoricoDescricao: 'Transcrições no histórico',
  cardSugestoes: 'Sugestões pendentes',
  cardSugestoesDescricao: 'Sugestões de IA aguardando curadoria',
  carregarMais: 'Carregar mais',
  tituloSecao: 'Histórico de transcrições',
  buscaPlaceholder: 'Buscar por título',
  filtroModoEu: 'Modo eu',
  // Vazio da TELA (nenhum grupo). Distinto de `semTranscricoes`, que é o vazio
  // de UM período já aberto.
  semResultados: 'Nenhuma transcrição encontrada',
}

// Um texto so, consumido pelo mixin da carteira do grupo, pelo modal de
// gerenciar e pela tabela de membros do CRM. Estava triplicado; a regra do repo
// manda extrair a partir de 3 consumidores.
export const TOOLTIP_GRUPO_SEM_PERMISSAO =
  'Só é possível editar grupos em que você atende todos os membros. Peça ao gestor do escritório.'

// Aba "Tarefas" do modal Lead/Cliente: falta de configuração (etapa de funil no
// lead, régua de relacionamento no cliente) é AVISO ACIONÁVEL, não bloqueio.
// O banner é inline e o conteúdo da aba continua renderizando.
export const CRM_TAREFAS_BANNER = {
  SEM_FUNIL: {
    titulo: 'Sem etapa de funil',
    mensagem:
      'Adicione uma etapa de funil para gerar as próximas tarefas e a linha do tempo automaticamente.',
    acao: 'Adicionar etapa de funil',
    dataTest: 'crm-tarefas-banner-sem-funil',
  },
  SEM_REGUA: {
    titulo: 'Sem régua de relacionamento',
    mensagem:
      'Vincule uma régua para gerar as próximas tarefas e a linha do tempo automaticamente.',
    acao: 'Vincular régua',
    dataTest: 'crm-tarefas-banner-sem-regua',
  },
}

// Duração mínima do spinner do CTA do banner: o modal abre na hora e sem isso o
// clique não teria feedback visível (UX Principle do CLAUDE.md do repo).
export const BANNER_ACTION_FEEDBACK_MS = 600

// Tooltips do CTA "Adicionar tarefa" da tabela "Próximas Tarefas".
export const TOOLTIP_ADICIONAR_TAREFA = 'Adicionar nova tarefa manual'
export const TOOLTIP_ADICIONAR_TAREFA_SEM_CLIENTE =
  'Vincule um cliente para criar tarefas manuais.'

// Aba "Tarefas" totalmente vazia (nem régua/funil, nem timeline, nem
// tarefas): substitui o banner + os 3 componentes empilhados (cada um com
// seu próprio "não há dados") por um único card de onboarding.
export const CRM_TAREFAS_EMPTY_STATE = {
  SEM_FUNIL: {
    titulo: 'Este lead ainda não tem uma etapa de funil',
    descricao:
      'Adicione uma etapa de funil para que as tarefas de relacionamento e a linha do tempo sejam geradas automaticamente.',
    acaoPrimaria: CRM_TAREFAS_BANNER.SEM_FUNIL.acao,
    acaoSecundaria: 'Adicionar tarefa manual',
    legenda:
      'Você também pode criar tarefas pontuais sem adicionar uma etapa de funil.',
    dataTest: 'crm-tarefas-empty-state-sem-funil',
  },
  SEM_REGUA: {
    titulo: 'Este cliente ainda não tem uma régua de relacionamento',
    descricao:
      'Vincule uma régua para que as tarefas de relacionamento e a linha do tempo sejam geradas automaticamente.',
    acaoPrimaria: CRM_TAREFAS_BANNER.SEM_REGUA.acao,
    acaoSecundaria: 'Adicionar tarefa manual',
    legenda: 'Você também pode criar tarefas pontuais sem vincular uma régua.',
    dataTest: 'crm-tarefas-empty-state-sem-regua',
  },
}

// Texto da drop-zone dos kanbans em modo fill-height (Leads / Oportunidades).
export const LEADS_KANBAN_DROP_ZONE_LABEL = 'Arraste um lead para cá'
export const OPPORTUNITIES_KANBAN_DROP_ZONE_LABEL =
  'Arraste uma oportunidade para cá'
